import type { Plugin } from "../../core/plugin";
import { PluginRegistry } from "../../core/pluginRegistry";

/**
 * wmpModal — diálogo para las landings WMP, portado desde el `pg-modal` de
 * Pegasus (apps-design-pegasus, `pegasus.ui.PgModals`).
 *
 * Mismo contrato que Pegasus — un disparador apunta a una plantilla oculta, y
 * el chrome de la modal se construye al abrirla — pero reescrito para este
 * repo: sin jQuery, sin handlers inline y sin delegación global, así que se
 * inicializa como cualquier otro plugin de Hydra y sobrevive a la CSP del
 * dominio WMP.
 *
 * Deliberadamente agnóstico de marca: solo emite clases `wmp-modal*`, así que
 * una segunda landing WMP reutiliza el comportamiento y solo escribe su
 * propia piel (ver `src/styles/wmp/shared/_modal.scss` y la piel de LTN al
 * lado).
 *
 * Contrato de marcado:
 *
 *   <button type="button"
 *           data-plugin="wmpModal"
 *           data-wmp-modal="#mi-modal"          ← selector de la plantilla
 *           [data-wmp-modal-class="wmp-modal--narrow"]   ← modificadores extra
 *           [data-wmp-modal-no-footer]>Abrir</button>
 *
 *   <div id="mi-modal" class="wmp-modal-source" aria-hidden="true">
 *     <div data-wmp-modal-title>Título</div>
 *     <div data-wmp-modal-caption>Subtítulo opcional bajo el título</div>
 *     <div data-wmp-modal-body>…contenido…</div>
 *     <div data-wmp-modal-footer>…acciones móvil…</div>
 *   </div>
 *
 * La plantilla se oculta con una clase, nunca con el atributo `hidden`: el
 * editor del CMS elimina los atributos que no conoce, y una plantilla que
 * pierda su `hidden` se renderizaría dos veces en la página.
 *
 * Todo lo que haya dentro de la plantilla con `data-plugin` se inicializa
 * mediante el MutationObserver de `PluginRegistry` cuando la copia llega al
 * documento, y se destruye cuando se elimina la modal — así que un
 * formulario dentro de la modal se comporta exactamente igual que uno
 * escrito en la página.
 */

const SCROLL_LOCK_CLASS = "wmp-modal-is-active";
const PART_TITLE = "[data-wmp-modal-title]";
const PART_CAPTION = "[data-wmp-modal-caption]";
const PART_BODY = "[data-wmp-modal-body]";
const PART_FOOTER = "[data-wmp-modal-footer]";
const CLOSE_TRANSITION_MS = 300;

const FOCUSABLE = [
  "a[href]",
  "button:not([disabled])",
  "input:not([disabled]):not([type='hidden'])",
  "select:not([disabled])",
  "textarea:not([disabled])",
  "[tabindex]:not([tabindex='-1'])",
].join(",");

let openModal: HTMLElement | null = null;
let lastTrigger: HTMLElement | null = null;
let modalSequence = 0;

function closeActiveModal(): void {
  if (!openModal) return;

  const modal = openModal;
  openModal = null;

  modal.classList.remove("is-open");
  document.documentElement.classList.remove(SCROLL_LOCK_CLASS);

  const trigger = lastTrigger;
  lastTrigger = null;

  window.setTimeout(() => {
    modal.remove();
    // El foco vuelve donde el usuario lo dejó, o la página saltaría arriba
    // en cuanto el diálogo desaparezca.
    trigger?.focus();
  }, CLOSE_TRANSITION_MS);
}

function buildCloseButton(label: string): HTMLButtonElement {
  const button = document.createElement("button");
  button.type = "button";
  button.className = "wmp-modal__close";
  button.setAttribute("aria-label", label);
  button.innerHTML =
    '<span class="wmp-modal__close-icon" aria-hidden="true"></span>';
  return button;
}

export class WmpModal implements Plugin {
  private modal: HTMLElement | null = null;

  private readonly onTriggerClick = (event: MouseEvent) => {
    // El disparador suele ser un <a href="#id"> para que el marcado
    // funcione sin JS; con el plugin activo, el salto de hash solo
    // competiría con el diálogo.
    if (this.element instanceof HTMLAnchorElement) event.preventDefault();
    this.open();
  };

  private readonly onModalClick = (event: MouseEvent) => {
    const target = event.target as HTMLElement;

    if (target.closest(".wmp-modal__close")) {
      event.preventDefault();
      this.close();
      return;
    }

    // Backdrop: el clic cayó en el overlay o en el padding alrededor del
    // diálogo, nunca dentro de él.
    if (!target.closest(".wmp-modal__dialog")) this.close();
  };

  private readonly onKeydown = (event: KeyboardEvent) => {
    if (!this.modal || this.modal !== openModal) return;
    if (event.key === "Escape") {
      this.close();
      return;
    }
    if (event.key === "Tab") this.trapFocus(event);
  };

  constructor(private readonly element: HTMLElement) {}

  init(): void {
    this.element.addEventListener("click", this.onTriggerClick);
  }

  destroy(): void {
    this.element.removeEventListener("click", this.onTriggerClick);
    if (this.modal === openModal) this.close();
  }

  /** Abre el diálogo al que apunta este disparador. Solo una modal a la vez. */
  open(): void {
    if (openModal) return;

    const selector = this.element.dataset.wmpModal;
    if (!selector) return;

    const source = document.querySelector<HTMLElement>(selector);
    if (!source) return;

    const modal = this.buildModal(source);

    document.body.appendChild(modal);
    this.modal = modal;
    openModal = modal;
    lastTrigger = this.element;
    document.documentElement.classList.add(SCROLL_LOCK_CLASS);
    document.addEventListener("keydown", this.onKeydown);
    modal.addEventListener("click", this.onModalClick);

    // Dos frames: el primero pinta la modal con opacidad 0, el segundo
    // añade la clase para que la transición se ejecute de verdad.
    requestAnimationFrame(() => {
      requestAnimationFrame(() => modal.classList.add("is-open"));
    });

    this.focusFirst(modal);
  }

  close(): void {
    document.removeEventListener("keydown", this.onKeydown);
    this.modal?.removeEventListener("click", this.onModalClick);
    if (this.modal === openModal) closeActiveModal();
    this.modal = null;
  }

  private buildModal(source: HTMLElement): HTMLElement {
    const title = source.querySelector(PART_TITLE);
    const caption = source.querySelector(PART_CAPTION);
    const body = source.querySelector(PART_BODY);
    const footer = source.querySelector(PART_FOOTER);

    const noFooter =
      !footer ||
      !footer.innerHTML.trim() ||
      source.hasAttribute("data-wmp-modal-no-footer") ||
      this.element.hasAttribute("data-wmp-modal-no-footer");

    const extraClass = this.element.dataset.wmpModalClass ?? "";
    const titleId = `wmp-modal-title-${++modalSequence}`;
    const captionId = `wmp-modal-caption-${modalSequence}`;
    const closeLabel = this.element.dataset.wmpModalCloseLabel ?? "Cerrar";

    const modal = document.createElement("div");
    modal.className = `wmp-modal${noFooter ? " wmp-modal--no-footer" : ""}${
      extraClass ? ` ${extraClass}` : ""
    }`;
    modal.setAttribute("role", "dialog");
    modal.setAttribute("aria-modal", "true");
    modal.tabIndex = -1;
    if (title) modal.setAttribute("aria-labelledby", titleId);
    if (caption) modal.setAttribute("aria-describedby", captionId);

    modal.innerHTML = [
      '<div class="wmp-modal__outer">',
      '<div class="wmp-modal__dialog">',
      '<div class="wmp-modal__header">',
      '<div class="wmp-modal__heading">',
      `<p class="wmp-modal__title" id="${titleId}"></p>`,
      `<p class="wmp-modal__caption" id="${captionId}"></p>`,
      "</div>",
      "</div>",
      '<div class="wmp-modal__body"></div>',
      '<div class="wmp-modal__footer"></div>',
      "</div>",
      "</div>",
    ].join("");

    const header = modal.querySelector(".wmp-modal__header") as HTMLElement;
    header.appendChild(buildCloseButton(closeLabel));

    // `innerHTML` y no un movimiento de nodo: la plantilla se queda en la
    // página, así que la modal se puede volver a abrir — y con sus campos
    // vacíos.
    if (title) {
      (modal.querySelector(".wmp-modal__title") as HTMLElement).innerHTML =
        title.innerHTML;
    }
    if (caption) {
      (modal.querySelector(".wmp-modal__caption") as HTMLElement).innerHTML =
        caption.innerHTML;
    } else {
      (modal.querySelector(".wmp-modal__caption") as HTMLElement).remove();
    }
    // Sin `data-wmp-modal-body` toda la plantilla es el body — menos el
    // título, el subtítulo y el footer, que se renderizan en sus propios
    // slots.
    (modal.querySelector(".wmp-modal__body") as HTMLElement).innerHTML = body
      ? body.innerHTML
      : [...source.children]
          .filter(
            (child) => child !== title && child !== caption && child !== footer,
          )
          .map((child) => child.outerHTML)
          .join("");
    if (footer && !noFooter) {
      (modal.querySelector(".wmp-modal__footer") as HTMLElement).innerHTML =
        footer.innerHTML;
    }

    return modal;
  }

  private focusFirst(modal: HTMLElement): void {
    const first = modal.querySelector<HTMLElement>(FOCUSABLE);
    (first ?? modal).focus({ preventScroll: true });
  }

  private trapFocus(event: KeyboardEvent): void {
    const focusable = [
      ...(this.modal?.querySelectorAll<HTMLElement>(FOCUSABLE) ?? []),
    ].filter((el) => el.offsetParent !== null);

    if (!focusable.length) return;

    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    const active = document.activeElement;

    if (event.shiftKey && (active === first || active === this.modal)) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && active === last) {
      event.preventDefault();
      first.focus();
    }
  }
}

PluginRegistry.register("wmpModal", WmpModal);
